import type { ReactNode } from 'react';
import { StyledEngineProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { BrowserRouter } from 'react-router-dom';
import { ColorModeProvider } from '@/theme/ColorModeProvider';
import { queryClient } from '@/lib/queryClient';
import { ConfirmProvider } from '@/components/feedback/ConfirmProvider';
import { AppErrorBoundary } from '@/components/feedback/AppErrorBoundary';
import { GlobalStateProvider } from '@/global/GlobalStateProvider';
import { SessionRestore } from '@/auth/SessionRestore';

/**
 * Wraps the entire app in the provider stack. Order matters:
 * styling engine → theme/CssBaseline → data layer → snackbars → confirm dialogs
 * → date localisation → global state → router.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <StyledEngineProvider injectFirst>
      <ColorModeProvider>
        <QueryClientProvider client={queryClient}>
          <SnackbarProvider
            maxSnack={3}
            autoHideDuration={4000}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <ConfirmProvider>
                <GlobalStateProvider>
                  <SessionRestore />
                  <BrowserRouter>
                    <AppErrorBoundary>{children}</AppErrorBoundary>
                  </BrowserRouter>
                </GlobalStateProvider>
              </ConfirmProvider>
            </LocalizationProvider>
          </SnackbarProvider>
        </QueryClientProvider>
      </ColorModeProvider>
    </StyledEngineProvider>
  );
}
